import { useCallback } from 'react';
import { User, ArrowLeft, Menu, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, LanguageSwitcher } from '@gokrew/ui';
import { usePageHeader } from '@/context/PageHeaderContext';
import { useAuth } from '@/context/AuthContext';
import { useUpdateAdminLanguage } from '@/hooks/useUpdateAdminLanguage';
import styles from './TopNav.module.css';

interface TopNavProps {
  onMenuClick: () => void;
  isMenuOpen: boolean;
}

export function TopNav({ onMenuClick, isMenuOpen }: TopNavProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { header } = usePageHeader();
  const { user } = useAuth();
  const { mutate: saveLanguage } = useUpdateAdminLanguage();

  const handleLanguageChange = useCallback(
    (code: string) => {
      saveLanguage(code);
    },
    [saveLanguage]
  );

  const handleBack = useCallback(() => {
    if (typeof header.backPath === 'number') navigate(header.backPath);
    else if (header.backPath) navigate(header.backPath);
  }, [header.backPath, navigate]);

  const handleProfile = useCallback(() => {
    navigate('/personal-information');
  }, [navigate]);

  return (
    <header className={styles.topNav}>
      <div className={styles.breadcrumb}>
        <Button
          type="button"
          variant="ghost"
          onClick={onMenuClick}
          className={`${styles.iconButton} ${styles.menuButton}`}
          aria-label={t('top_nav.open_menu')}
          aria-expanded={isMenuOpen}
          aria-controls="admin-sidebar"
        >
          <Menu className={styles.icon} aria-hidden="true" />
        </Button>
        {header.backPath !== undefined && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            className={styles.iconButton}
            aria-label={t('top_nav.go_back')}
          >
            <ArrowLeft className={styles.icon} aria-hidden="true" />
          </Button>
        )}
        <span className={styles.workspace}>{t('top_nav.workspace')}</span>
        <ChevronRight className={styles.separator} aria-hidden="true" />
        <span className={styles.title}>{header.title}</span>
      </div>
      <div className={styles.controls}>
        <LanguageSwitcher onLanguageChange={handleLanguageChange} />
        <Button
          type="button"
          variant="ghost"
          onClick={handleProfile}
          className={styles.profile}
          aria-label={t('top_nav.open_profile')}
          title={user?.name || t('top_nav.open_profile')}
        >
          <span className={styles.avatar} aria-hidden="true">
            <User className={styles.icon} />
          </span>
          <span className={styles.profileText}>
            <strong>{user?.name}</strong>
            <span>
              {t(
                user?.role === 'super_admin'
                  ? 'top_nav.super_admin'
                  : 'top_nav.admin'
              )}
            </span>
          </span>
        </Button>
      </div>
    </header>
  );
}
